import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FaqService } from '../../../../../../shared/services/faq.service';
import { IFaq, IFaqVisibility } from '@shared/interfaces';
import { AdminActionsDirective } from '../../../../directives/admin-actions.directive';
import { AdminService } from '../../../../admin.service';
import { AdminFaqService } from '../../services/admin-faq.service';
import { SnackService } from '../../../../../../shared/services/snack.service';
import { DialogService } from '../../../../../../shared/services/dialog.service';
import { ModuleService } from '../../../../../../shared/services/module.service';
import { FileSaverService } from 'ngx-filesaver';
import { HttpClient } from '@angular/common/http';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../../../../current-project';
import { FaqOperations } from '../../operations/admin-faq.operations';

@Component({
    selector: 'app-admin-faq-edit',
    templateUrl: './admin-faq-edit.component.html',
    styleUrls: ['./admin-faq-edit.component.scss'],
    standalone: false
})
export class AdminFaqEditComponent extends AdminActionsDirective implements OnInit {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    visibilities = [IFaqVisibility.All, IFaqVisibility.Admin];
    languages = this.availableLanguages;
    formGroup: UntypedFormGroup;
    currentItem: IFaq;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private snack: SnackService,
        private translate: TranslateService,
        private faqService: FaqService,
        private adminFaqService: AdminFaqService,
        adminService: AdminService,
        dialogService: DialogService,
        moduleService: ModuleService,
        private http: HttpClient,
        private fileSaver: FileSaverService,
        private faqOperations: FaqOperations
    ) {
        super(adminService, dialogService, moduleService);
    }

    get value(): IFaq {
        return this.formGroup.value;
    }

    ngOnInit(): void {
        this.buildForm();
    }

    documentsToUpload(key: string): File[] {
        return this.formGroup.get(key).value;
    }

    languageGroup(key?: string): UntypedFormGroup {
        return this.formBuilder.group(
            this.languages
                .map(e => ({
                    [e]: [key ? this.item[key][e] : '', e === 'en' ? Validators.required : null]
                }))
                .reduce((a, b) => ({ ...a, ...b }), {})
        );
    }

    title(language: string): UntypedFormControl {
        return (this.formGroup?.controls?.title as UntypedFormGroup)?.controls[language] as UntypedFormControl;
    }

    content(language: string): UntypedFormControl {
        return (this.formGroup?.controls?.content as UntypedFormGroup)?.controls[language] as UntypedFormControl;
    }

    get isParent(): boolean {
        return !this.formGroup.get('content');
    }

    changeChildren(hasChildren: boolean) {
        if (hasChildren) {
            this.formGroup.removeControl('content');
        } else {
            this.formGroup.addControl('content', this.languageGroup());
            this.formGroup.patchValue({ hasGuidance: false, videoLink: '' });
        }
    }

    buildForm() {
        this.currentItem = this.adminFaqService.currentItem;
        const visibility = this.currentItem?.visibility;
        if (visibility === IFaqVisibility.Admin) {
            this.visibilities = [IFaqVisibility.Admin];
        }

        if (IS_TRS_PROJECT) {
            this.formGroup = this.formBuilder.group({
                id: this.item.id,
                title: this.languageGroup('title'),
                content: this.item.content ? this.languageGroup('content') : null,
                visibility: new FormControl<IFaqVisibility>(this.item.visibility, Validators.required),
                hasGuidance: this.item.hasGuidance,
                videoLink: this.item.videoLink,
                documents: [this.item.documents]
            });
        }

        if (IS_PRODUCT_SAFETY_PROJECT) {
            this.formGroup = this.formBuilder.group({
                id: this.item.id,
                title: this.languageGroup('title'),
                content: this.item.content ? this.languageGroup('content') : null,
                visibility: new FormControl<IFaqVisibility>(this.item.visibility, Validators.required),
                hasGuidance: this.item.hasGuidance,
                videoLink: this.item.videoLink,
                enFilePath: this.item.enFilePath,
                deFilePath: this.item.deFilePath,
                documentsDE: [this.item.documentsDE],
                documentsEN: [this.item.documentsEN]
            });
        }
    }

    get item(): IFaq {
        return this.selectedItem;
    }

    async updateItem(value: IFaq, faqService: FaqService, snack: SnackService): Promise<void> {
        await faqService.updateItem(value);
        snack.openSnackBarSuccess('admin.faq.snacks.updateSuccess');
    }

    async save(event: any): Promise<void> {
        this.faqOperations.formGroup = this.formGroup;
        await this.faqOperations.save(event, this.updateItem, this.value);
    }
}
